import React, { useEffect, useState } from 'react'
import { Col, DatePicker, Input, Radio, Row, Typography, Upload, UploadFile } from 'antd'
import type { RangePickerProps } from 'antd/es/date-picker'
import { RcFile, UploadProps } from 'antd/lib/upload'
import dayjs from 'dayjs'
import { NumericFormat } from 'react-number-format'
import { Add } from 'iconsax-react'
import axios from 'axios'
import utc from 'dayjs/plugin/utc'
import { classNameInput, classNameRadioGroup } from 'helpers/formik/classNameForm'
import { imageType, isSizeMaxAllowed, isSizeMinAllowed } from 'helpers/imageUpload'
import { Configuration, MediaApi, VoucherTypeEnum } from 'generated/api'
import { FormVoucherInfoProps } from './InterfaceForm'
import { axiosDefaultConfig } from 'constants/axios'

dayjs.extend(utc)

const axiosConfig = new Configuration(axiosDefaultConfig)
const mediaApi = new MediaApi(axiosConfig)

export default function FormSection1(props: FormVoucherInfoProps) {
  const [fileList, setFileList] = useState<UploadFile[]>([])

  useEffect(() => {
    if (props.formikProps.values.imageUrl) {
      const updatedFileList = [
        {
          uid: '1',
          name: props.formikProps.values.imageUrl,
          status: 'done',
          url: props.formikProps.values.imageUrl,
        },
      ]
      setFileList(updatedFileList as unknown as UploadFile[])
    }
  }, [props.formikProps.values.imageUrl])

  const radioType = [
    {
      value: VoucherTypeEnum.Public,
      additionalClassName: classNameRadioGroup(
        props.formikProps.values.type,
        VoucherTypeEnum.Public,
      ),
      title: 'Publik',
      subtitle: 'Digunakan semua pembeli untuk bertransaksi di aplikasi SawitPRO.',
    },
    {
      value: VoucherTypeEnum.Private,
      additionalClassName: classNameRadioGroup(
        props.formikProps.values.type,
        VoucherTypeEnum.Private,
      ),
      title: 'Khusus',
      subtitle: 'Digunakan pembeli yang menerima kode khusus dari kupon yang dibuat.',
    },
  ]

  const handleChangeStartDate = async (_date: dayjs.Dayjs | null, dateString: string) => {
    const utcDate = dayjs(`${dateString} 00:00:00`, 'DD/MM/YYYY HH:mm:ss').toDate().valueOf()
    await props.formikProps.setFieldValue('startAtUtc0', utcDate || 0)
    await props.formikProps.setFieldValue('endAtUtc0', 0)
    if (utcDate) props.formikProps.setFieldError('startAtUtc0', '')
  }

  const handleChangeEndDate = async (_date: dayjs.Dayjs | null, dateString: string) => {
    const utcDate = dayjs(`${dateString} 23:59:59`, 'DD/MM/YYYY HH:mm:ss').toDate().valueOf()
    await props.formikProps.setFieldValue('endAtUtc0', utcDate || 0)
    if (utcDate) props.formikProps.setFieldError('endAtUtc0', '')
  }

  const disabledDateStart: RangePickerProps['disabledDate'] = current => {
    return current && current.valueOf() <= dayjs().subtract(1, 'day').endOf('day').valueOf()
  }

  const disabledDateEnd: RangePickerProps['disabledDate'] = current => {
    const compare = props.formikProps.values.startAtUtc0
      ? dayjs(props.formikProps.values.startAtUtc0).subtract(1, 'day').endOf('day').valueOf()
      : dayjs().subtract(1, 'day').endOf('day').valueOf()
    return current && current.valueOf() <= compare
  }

  const uploadProps: UploadProps = {
    accept: imageType.join(','),
    showUploadList: {
      showRemoveIcon: false,
    },
    fileList: fileList,
    maxCount: 1,
    multiple: false,
    beforeUpload: async (file: RcFile) => {
      const isSizeMinimumAllowed = isSizeMinAllowed(file)
      const isSizeMaximumAllowed = isSizeMaxAllowed(file)

      if (!isSizeMinimumAllowed) {
        await props.formikProps.setTouched({ imageUrl: true })
        props.formikProps.setFieldError('imageUrl', `${file.name} minimal ukuran lebih dari 1KB`)
        return false
      }

      if (!isSizeMaximumAllowed) {
        await props.formikProps.setTouched({ imageUrl: true })
        props.formikProps.setFieldError('imageUrl', `${file.name} melebihi ukuran maksimal 2MB`)
        return false
      }

      return (isSizeMaximumAllowed && isSizeMinimumAllowed) || Upload.LIST_IGNORE
    },
    customRequest: async ({ file }) => {
      const uploadFile = file as RcFile
      const updatedFileList = [
        { uid: uploadFile.uid, name: uploadFile.name, status: 'uploading', percent: 0 },
      ]
      try {
        setFileList(updatedFileList as UploadFile[])
        const date = dayjs().toDate().valueOf()
        const responsePresign = await mediaApi.mediaPresignedUrl({
          operationType: 'Ecommerce',
          mimeType: uploadFile.type,
          originalFileName: `voucher/${date}`,
          fileSize: uploadFile.size,
        })
        await axios.put(responsePresign.data.presignedUrl, file, {
          headers: {
            'Content-Type': uploadFile.type,
          },
          onUploadProgress: progressEvent => {
            if (progressEvent.total) {
              const percentCompleted = Math.round(
                (progressEvent.loaded * 100) / progressEvent.total,
              )
              const updatedFileListProgress = updatedFileList.map(item => {
                if (item.uid === uploadFile.uid) {
                  return { ...item, percent: percentCompleted }
                }
                return item
              })
              setFileList(updatedFileListProgress as UploadFile[])
            }
          },
        })

        const updatedFileListDone = updatedFileList.map(item => {
          if (item.uid === uploadFile.uid) {
            return {
              uid: uploadFile.uid,
              name: uploadFile.name,
              status: 'done',
              url: responsePresign.data.publicUrl,
            }
          }
          return item
        })
        const updatedImageUrlItems = responsePresign.data.publicUrl
        setFileList(updatedFileListDone as UploadFile[])
        await props.formikProps.setFieldValue('imageUrl', updatedImageUrlItems)
      } catch (err) {
        const updatedFileListError = updatedFileList.map(item => {
          if (item.uid === uploadFile.uid) {
            return { ...item, status: 'error' }
          }
          return item
        })
        setFileList(updatedFileListError as UploadFile[])
      }
    },
  }

  const disabledForm = props.disabledForm

  return (
    <Row className="flex flex-col gap-9">
      <Row gutter={[8, 8]}>
        <Col span={24} lg={6} className="lg:flex lg:self-center">
          <Typography.Text className="font-medium text-base">Target Voucher</Typography.Text>
        </Col>
        <Col span={24} lg={18}>
          <Radio.Group
            value={props.formikProps.values.type}
            onChange={e => {
              const value = e.target.value
              props.formikProps.setFieldValue('type', value)
              props.formikProps.setFieldValue('code', '')
            }}
          >
            <Row gutter={24}>
              {radioType.map(item => (
                <Col span={12} key={item.title}>
                  <Radio
                    disabled={disabledForm}
                    data-testid={`radio_type_${item.value}`}
                    key={item.value}
                    value={item.value}
                    className={item.additionalClassName}
                  >
                    <Typography.Paragraph className="font-semibold text-base my-0 text-[#3C5337]">
                      {item.title}
                    </Typography.Paragraph>
                    <Typography.Paragraph className="font-normal text-xs my-0">
                      {item.subtitle}
                    </Typography.Paragraph>
                  </Radio>
                </Col>
              ))}
            </Row>
          </Radio.Group>
        </Col>
      </Row>
      <Row gutter={[8, 8]}>
        <Col span={24}>
          <Typography.Paragraph className="font-medium text-base my-0">
            Nama Voucher
          </Typography.Paragraph>
        </Col>
        <Col span={24} lg={6}>
          <Typography.Paragraph className="font-normal text-xs my-0">
            Nama Voucher akan menjadi judul utama yang dilihat oleh pembeli
          </Typography.Paragraph>
        </Col>
        <Col span={24} lg={9}>
          <Input
            placeholder="Masukkan Nama Voucher"
            data-testid="input_name"
            className={classNameInput('name', props.formikProps)}
            value={props.formikProps.values.name}
            onChange={e => {
              props.formikProps.setFieldTouched('name')
              props.formikProps.setFieldValue('name', e.target.value)
            }}
            status={props.formikProps.errors.name && props.formikProps.touched.name ? 'error' : ''}
            disabled={disabledForm}
          />
          {props.formikProps.errors.name && props.formikProps.touched.name ? (
            <Typography.Text
              className="text-[#FF4D4D] text-xs font-normal"
              data-testid="error_name"
            >
              {props.formikProps.errors.name}
            </Typography.Text>
          ) : null}
        </Col>
      </Row>
      <Row gutter={[8, 8]}>
        <Col span={24}>
          <Typography.Paragraph className="font-medium text-base my-0">Label</Typography.Paragraph>
        </Col>
        <Col span={24} lg={6}>
          <Typography.Paragraph className="font-normal text-xs my-0">
            Gunakan kalimat dalam penekanan potongan harga
          </Typography.Paragraph>
        </Col>
        <Col span={24} lg={9}>
          <Input
            placeholder="Masukkan Label Voucher"
            data-testid="input_label"
            className={classNameInput('label', props.formikProps)}
            value={props.formikProps.values.label}
            onChange={e => {
              props.formikProps.setFieldTouched('label')
              props.formikProps.setFieldValue('label', e.target.value)
            }}
            status={
              props.formikProps.errors.label && props.formikProps.touched.label ? 'error' : ''
            }
            disabled={disabledForm}
          />
          {props.formikProps.errors.label && props.formikProps.touched.label ? (
            <Typography.Text
              className="text-[#FF4D4D] text-xs font-normal"
              data-testid="error_label"
            >
              {props.formikProps.errors.label}
            </Typography.Text>
          ) : null}
        </Col>
      </Row>
      <Row gutter={[8, 8]}>
        <Col span={24} lg={6}>
          <Typography.Paragraph className="font-medium text-base my-0">
            Stok Voucher
          </Typography.Paragraph>
        </Col>
        <Col span={24} lg={9}>
          <NumericFormat
            placeholder="0"
            data-testid="input_maxRedemption"
            className={classNameInput('maxRedemption', props.formikProps)}
            value={props.formikProps.values.maxRedemption || null}
            onValueChange={val =>
              props.formikProps.setFieldValue('maxRedemption', val.floatValue ?? 0)
            }
            status={
              props.formikProps.errors.maxRedemption && props.formikProps.touched.maxRedemption
                ? 'error'
                : ''
            }
            disabled={disabledForm}
            customInput={Input}
            allowNegative={false}
            decimalScale={0}
            thousandSeparator="."
            decimalSeparator=","
          />
          {props.formikProps.errors.maxRedemption && props.formikProps.touched.maxRedemption ? (
            <Typography.Text
              className="text-[#FF4D4D] text-xs font-normal"
              data-testid="error_maxRedemption"
            >
              {props.formikProps.errors.maxRedemption}
            </Typography.Text>
          ) : null}
        </Col>
      </Row>
      {props.formikProps.values.type === 'PRIVATE' ? (
        <Row gutter={[8, 8]}>
          <Col span={24} lg={6}>
            <Typography.Paragraph className="font-medium text-base my-0">
              Kode Voucher
            </Typography.Paragraph>
          </Col>
          <Col span={24} lg={9}>
            <Input
              placeholder="Masukkan Kode Voucher"
              data-testid="input_code"
              className={classNameInput('code', props.formikProps)}
              value={props.formikProps.values.code}
              onChange={e => {
                props.formikProps.setFieldTouched('code')
                props.formikProps.setFieldValue('code', e.target.value)
              }}
              status={
                props.formikProps.errors.code && props.formikProps.touched.code ? 'error' : ''
              }
              disabled={disabledForm}
            />
            {props.formikProps.errors.code && props.formikProps.touched.code ? (
              <Typography.Text
                className="text-[#FF4D4D] text-xs font-normal"
                data-testid="error_code"
              >
                {props.formikProps.errors.code}
              </Typography.Text>
            ) : null}
          </Col>
        </Row>
      ) : null}
      <Row gutter={[8, 8]}>
        <Col span={24} lg={6}>
          <Typography.Paragraph className="font-medium text-base my-0">
            Batas Penggunaan / User
          </Typography.Paragraph>
        </Col>
        <Col span={24} lg={9}>
          <NumericFormat
            placeholder="Contoh: 1"
            data-testid="input_maxRedemptionPerUser"
            className={classNameInput('maxRedemptionPerUser', props.formikProps)}
            value={props.formikProps.values.maxRedemptionPerUser || null}
            onValueChange={val =>
              props.formikProps.setFieldValue('maxRedemptionPerUser', val.floatValue ?? 0)
            }
            status={
              props.formikProps.errors.maxRedemptionPerUser &&
              props.formikProps.touched.maxRedemptionPerUser
                ? 'error'
                : ''
            }
            disabled={disabledForm}
            customInput={Input}
            allowNegative={false}
            decimalScale={0}
            thousandSeparator="."
            decimalSeparator=","
          />
          {props.formikProps.errors.maxRedemptionPerUser &&
          props.formikProps.touched.maxRedemptionPerUser ? (
            <Typography.Text
              className="text-[#FF4D4D] text-xs font-normal"
              data-testid="error_maxRedemptionPerUser"
            >
              {props.formikProps.errors.maxRedemptionPerUser}
            </Typography.Text>
          ) : null}
        </Col>
      </Row>
      <Row gutter={[8, 8]}>
        <Col span={24} lg={6}>
          <Typography.Paragraph className="font-medium text-base my-0">
            Penggunaan Sawitpoin
          </Typography.Paragraph>
        </Col>
        <Col span={24} lg={18}>
          <Radio.Group
            value={props.formikProps.values.isSawitPoin}
            onChange={e => props.formikProps.setFieldValue('isSawitPoin', e.target.value)}
          >
            <div className="flex">
              <Radio value={true} data-testid="radio_isSawitPoin_true" disabled={disabledForm}>
                Boleh Pakai SawitPoin
              </Radio>
              <Radio value={false} data-testid="radio_isSawitPoin_false" disabled={disabledForm}>
                Tidak boleh pakai Sawitpoin
              </Radio>
            </div>
          </Radio.Group>
        </Col>
      </Row>
      <Row gutter={[8, 8]}>
        <Col span={24} lg={6}>
          <Typography.Paragraph className="font-medium text-base my-0">
            Deskripsi
          </Typography.Paragraph>
        </Col>
        <Col span={24} lg={9}>
          <Input.TextArea
            placeholder="Masukkan deskripsi singkat tentang Voucher ini..."
            data-testid="input_description"
            className={classNameInput('description', props.formikProps)}
            value={props.formikProps.values.description}
            onChange={e => {
              props.formikProps.setFieldTouched('description')
              props.formikProps.setFieldValue('description', e.target.value)
            }}
            status={
              props.formikProps.errors.description && props.formikProps.touched.description
                ? 'error'
                : ''
            }
            disabled={disabledForm}
          />
          {props.formikProps.errors.description && props.formikProps.touched.description ? (
            <Typography.Text
              className="text-[#FF4D4D] text-xs font-normal"
              data-testid="error_description"
            >
              {props.formikProps.errors.description}
            </Typography.Text>
          ) : null}
        </Col>
      </Row>
      <Row gutter={[8, 8]}>
        <Col span={24} lg={6} className="lg:flex lg:self-center">
          <Typography.Paragraph className="font-medium text-base my-0">
            Periode Voucher Aktif
          </Typography.Paragraph>
        </Col>
        <Col span={24} lg={9}>
          <Row gutter={8}>
            <Col span={24} lg={12}>
              <Typography.Paragraph className="font-bold text-xs text-[#333539] mb-2">
                Tanggal Mulai
              </Typography.Paragraph>
              <DatePicker
                placeholder="Pilih Tanggal"
                data-testid="datepicker_startAtUtc0"
                className={`${classNameInput('startAtUtc0', props.formikProps)} w-full`}
                value={
                  props.formikProps.values.startAtUtc0
                    ? dayjs.utc(props.formikProps.values.startAtUtc0).local()
                    : undefined
                }
                onChange={(date, dateString) => handleChangeStartDate(date, dateString as string)}
                status={
                  props.formikProps.errors.startAtUtc0 && props.formikProps.touched.startAtUtc0
                    ? 'error'
                    : ''
                }
                format="DD/MM/YYYY"
                disabledDate={disabledDateStart}
                disabled={disabledForm}
              />
              {props.formikProps.errors.startAtUtc0 && props.formikProps.touched.startAtUtc0 ? (
                <Typography.Text
                  className="text-[#FF4D4D] text-xs font-normal"
                  data-testid="error_startAtUtc0"
                >
                  {props.formikProps.errors.startAtUtc0}
                </Typography.Text>
              ) : null}
            </Col>
            <Col span={24} lg={12}>
              <Typography.Paragraph className="font-bold text-xs text-[#333539] mb-2">
                Tanggal Berakhir
              </Typography.Paragraph>
              <DatePicker
                placeholder="Pilih Tanggal"
                data-testid="datepicker_endAtUtc0"
                className={`${classNameInput('endAtUtc0', props.formikProps)} w-full`}
                value={
                  props.formikProps.values.endAtUtc0
                    ? dayjs.utc(props.formikProps.values.endAtUtc0).local()
                    : undefined
                }
                onChange={(date, dateString) => handleChangeEndDate(date, dateString as string)}
                status={
                  props.formikProps.errors.endAtUtc0 && props.formikProps.touched.endAtUtc0
                    ? 'error'
                    : ''
                }
                format="DD/MM/YYYY"
                disabledDate={disabledDateEnd}
                disabled={disabledForm}
              />
              {props.formikProps.errors.endAtUtc0 && props.formikProps.touched.endAtUtc0 ? (
                <Typography.Text
                  className="text-[#FF4D4D] text-xs font-normal"
                  data-testid="error_endAtUtc0"
                >
                  {props.formikProps.errors.endAtUtc0}
                </Typography.Text>
              ) : null}
            </Col>
          </Row>
        </Col>
      </Row>
      <Row gutter={[8, 8]}>
        <Col span={24} lg={6} className="lg:flex lg:self-center">
          <Typography.Paragraph className="font-medium text-base my-0">
            Unggah Foto
          </Typography.Paragraph>
        </Col>
        <Col span={24} lg={18}>
          <Upload
            listType="picture-card"
            className="font-medium"
            {...uploadProps}
            data-testid="button_upload"
          >
            {disabledForm ? null : (
              <button
                className="hover:cursor-pointer"
                style={{ border: 0, background: 'none' }}
                type="button"
              >
                <Add size={14} />
                <div className="mt-2 text-[#000] opacity-45">Upload</div>
              </button>
            )}
          </Upload>
          {props.formikProps.errors.imageUrl && props.formikProps.touched.imageUrl ? (
            <Typography.Text
              className="text-[#FF4D4D] text-xs font-normal"
              data-testid="error_imageUrl"
            >
              {props.formikProps.errors.imageUrl}
            </Typography.Text>
          ) : null}
        </Col>
      </Row>
    </Row>
  )
}

FormSection1.defaultProps = {
  disabledForm: false,
}
